import EraType from "@/roc/era/EraType";
import PassiveAura from "@/roc/effects/PassiveAura/PassiveAura";
import { useLayoutStore } from "@/roc/stores/LayoutStore";
import { useWonderStore } from "@/roc/stores/WonderStore";
import PassiveAuraType from "@/roc/effects/PassiveAura/PassiveAuraType";
import { applyWonderSynergies } from "@/components/Bonuses/Calculator/Bonus";
class PassiveBonusCalculator {
    static calculate(playerEra) {
        const lytStore = useLayoutStore();
        const wonderStore = useWonderStore();
        const result = [];
        const stats = {};
        const processEffect = function (effect, amount) {
            switch (effect.type) {
                case PassiveAuraType.RESEARCH_POINT_GENERATION_TIME_BONUS:
                case PassiveAuraType.GRANTS_TRADE_WORKER:
                case PassiveAuraType.DOCK_WORKER_TIME_BONUS:
                case PassiveAuraType.TREASURE_HUNT_ATTEMPT_GENERATION_TIME_BONUS:
                case PassiveAuraType.TREASURE_HUNT_ATTEMPTS_CAP:
                case PassiveAuraType.RESEARCH_POINT_GENERATION_CAP:
                case PassiveAuraType.GRANTS_CAPITAL_CITY_WORKER:
                case PassiveAuraType.GRANTS_ARABIA_WORKER:
                case PassiveAuraType.EXTRA_COMMANDER_SLOT:
                case PassiveAuraType.GOLD_PRODUCTION_MOD:
                case PassiveAuraType.FOOD_PRODUCTION_MOD:
                case PassiveAuraType.CAPITAL_CITY_GOODS_PRODUCTION_MOD:
                case PassiveAuraType.TRADE_OFFER_MOD:
                case PassiveAuraType.GEARS_FROM_WONDER_CONTRIBUTION_MOD:
                case PassiveAuraType.EGYPT_PRODUCTION_MOD:
                case PassiveAuraType.CHINA_PRODUCTION_MOD:
                case PassiveAuraType.MAYA_PRODUCTION_MOD:
                case PassiveAuraType.VIKINGS_TAVERN_PRODUCTION_MOD:
                case PassiveAuraType.ARABIA_GOODS_PRODUCTION_MOD:
                case PassiveAuraType.ARABIAN_BAZAAR_OFFERS_MOD:
                    stats[effect.type] = (stats[effect.type] || 0) + amount;
                    break;
                default:
                    break;
            }
        };
        lytStore.mapBuildings((building, buildingId) => {
            for (let i = 0; i < building.effects.count(); i++) {
                const effect = building.effects.getEffect(i);
                if (effect instanceof PassiveAura) {
                    const amount = effect.getAmount(building.level || 1, building.era || playerEra);
                    if (!amount) {
                        continue;
                    }
                    processEffect(effect, amount);
                }
            }
            return true;
        });
        wonderStore.mapWonders((wonder, config) => {
            for (let i = 0; i < wonder.effects.count(); i++) {
                if (config.active) {
                    const effect = wonder.effects.getEffect(i);
                    if (effect instanceof PassiveAura) {
                        const amount = effect.getAmount(config.level, EraType.stoneAge /*Note: Era is unused for wonders*/);
                        if (amount) {
                            processEffect(effect, amount);
                            applyWonderSynergies(wonder, config.level, i, processEffect);
                        }
                    }
                }
            }
            return true;
        });
        for (const i in stats) {
            const amount = stats[i];
            switch (i) {
                case PassiveAuraType.RESEARCH_POINT_GENERATION_TIME_BONUS:
                    result.push({
                        icon: 'RP',
                        overlay: 'StatRecruitmentTime',
                        description: `Reduce <b>Research Point</b> generation time by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.GRANTS_TRADE_WORKER:
                    result.push({
                        icon: 'DockWorker',
                        description: `Gain <b>${amount}</b> <b>Dock Worker${amount > 1 ? 's' : ''}</b>.`,
                    });
                    break;
                case PassiveAuraType.DOCK_WORKER_TIME_BONUS:
                    result.push({
                        icon: 'DockWorkerBoost',
                        description: `Reduce the cooldown of <b>Trade offers</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.TREASURE_HUNT_ATTEMPT_GENERATION_TIME_BONUS:
                    result.push({
                        icon: 'Compass',
                        overlay: 'StatRecruitmentTime',
                        description: `Reduce <b>Treasure Hunt Attempt</b> generation time by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.TREASURE_HUNT_ATTEMPTS_CAP:
                    result.push({
                        icon: 'Compass',
                        overlay: 'StatCap',
                        description: `Increase <b>Treasure Hunt Attempt</b> cap to <b>${4 + amount}</b>.`,
                    });
                    break;
                case PassiveAuraType.RESEARCH_POINT_GENERATION_CAP:
                    result.push({
                        icon: 'RP',
                        overlay: 'StatCap',
                        description: `Increase <b>Research Point</b> cap to <b>${amount}</b>.`,
                    });
                    break;
                case PassiveAuraType.GRANTS_CAPITAL_CITY_WORKER:
                    result.push({
                        icon: 'Worker',
                        description: `Gain <b>${amount}</b> <b>Worker${amount > 1 ? 's' : ''}</b> in the <b>Capital City</b>.`,
                    });
                    break;
                case PassiveAuraType.GRANTS_ARABIA_WORKER:
                    result.push({
                        icon: 'ArabiaWorker',
                        description: `Gain <b>${amount}</b> <b>Worker${amount > 1 ? 's' : ''}</b> in <b>Arabia</b>.`,
                    });
                    break;
                case PassiveAuraType.EXTRA_COMMANDER_SLOT:
                    result.push({
                        icon: 'CommanderSlot',
                        description: `Gain an additional <b>Commander Slot</b>.`,
                    });
                    break;
                case PassiveAuraType.GOLD_PRODUCTION_MOD:
                    result.push({
                        icon: 'Coin',
                        description: `Increase the amount of <b>Gold</b> produced by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.FOOD_PRODUCTION_MOD:
                    result.push({
                        icon: 'Food',
                        description: `Increase the amount of <b>Food</b> produced by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.CAPITAL_CITY_GOODS_PRODUCTION_MOD:
                    result.push({
                        icon: 'Goods',
                        description: `Increase the amount of all <b>Goods</b> produced in <b>Capital City</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.TRADE_OFFER_MOD:
                    result.push({
                        icon: 'TradeOffer',
                        description: `Increase the amount of <b>Goods</b> received from <b>Trade Offers</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.GEARS_FROM_WONDER_CONTRIBUTION_MOD:
                    result.push({
                        icon: 'Gears',
                        overlay: 'StatCap',
                        description: `Increase the amount of <b>Gears</b> gained from <b>Wonder Contributions</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.EGYPT_PRODUCTION_MOD:
                    result.push({
                        icon: 'EgyptGoods',
                        description: `Increase the amount of all <b>Goods</b> produced in <b>Egypt</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.CHINA_PRODUCTION_MOD:
                    result.push({
                        icon: 'ChinaGoods',
                        description: `Increase the amount of all <b>Goods</b> produced in <b>China</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.MAYA_PRODUCTION_MOD:
                    result.push({
                        icon: 'MayaGoods',
                        description: `Increase the amount of all <b>Goods</b> produced in <b>Maya Empire</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.VIKINGS_TAVERN_PRODUCTION_MOD:
                    result.push({
                        icon: 'Mead',
                        description: `Increase the goods produced by <b>Taverns</b> in <b>Viking Kingdom</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.ARABIA_GOODS_PRODUCTION_MOD:
                    result.push({
                        icon: 'ArabiaGoods',
                        description: `Increase the amount of all <b>Goods</b> produced in <b>Arabia</b> by <b>${amount}%</b>.`,
                    });
                    break;
                case PassiveAuraType.ARABIAN_BAZAAR_OFFERS_MOD:
                    result.push({
                        icon: 'Bazaar',
                        description: `Increase the amount of resources for <b>Bazaar Offers</b> when using the <b>Arabian Bazaar</b> by <b>${amount}%</b>.`,
                    });
                    break;
            }
        }
        return result;
    }
}
export default PassiveBonusCalculator;
